// Utils
import { vuelidateUtil } from '@/utils/vuelidate-util.js';

// Constants
import { GA } from '@/app/constants/ga-constants';
import { ERROR_MSG, STATUS_CODES, STATUS_MSG, USER_ROLES } from '@/app/constants/status-constants';

// Services
import AuthSetPasswordService from '@/app/services/auth/auth-set-password/auth-set-password-service.js';
import UserService from '@/app/services/main/user/user-service.js';

// Mixins
import { GoogleAnalyticsMixins } from '@/app/mixins/utils/google-analytics-mixins';
import { toastMessageMixins } from '@/app/mixins/utils/toast-message-mixins';
import { userMixins } from '@/app/mixins/modules/user-mixins.js';

// Third party libraries
let jwt = require('jsonwebtoken');

export default {
	name: 'AuthSetPassword',
	mixins: [
		GoogleAnalyticsMixins,
		toastMessageMixins,
		userMixins
	],
	data() {
		return {
			currentPass: '',
			showCurrentPassword: false,
			showPassword: false,
			showConfirmPassword: false,
			password: '',
			confirmPassword: '',
			isAPILoading: false,
			mode: '',
			hasUppercase: false,
			hasLowercase: false,
			hasNumber: false,
			has6Char: false
		}
	},
	computed: {
		validationRules() {
			let fields = {
				currentPass: {},
				password: {},
				confirmPassword: {}
			}

			fields = vuelidateUtil.setFieldsRequired(fields, true);
			fields = vuelidateUtil.setMinLengthValidation(fields, 'password', 6);
			fields = vuelidateUtil.setHasUpperCase(fields, 'password');
			fields = vuelidateUtil.setHasNumber(fields, 'password');
			// reset validation for current password
			if (this.$route.path !== '/change-password') {
				fields.currentPass = {};
			}

			return fields;
		},
		modeText() {
			let header = '';
			
			if (this.mode === 'reset') {
				header = 'Reset Password';
			} else if (this.mode === 'change') {
				header = 'Change Password';
			} else {
				header = 'Set Password';
			}

			return header;
		}
	},
	watch: {
		'$route': {
			immediate: true,
			handler(url) {
				// set Email token and JWT from URL
				this.initializeAuthService(url.query);

				if(url.path === '/reset-password') {
					this.mode = 'reset';
				} else if(url.path === '/change-password') {
					this.mode = 'change';
				} else {
					this.mode = 'set';
				}
			}
		},
		password(value){
			const contains6Characters = new RegExp('(?=.{6,})')
			const containsUppercase = new RegExp('(?=.*[A-Z])')
			const containsLowercase = new RegExp('(?=.*[a-z])')
			const containsNumber = new RegExp('(?=.*[0-9])')

			this.has6Char = contains6Characters.test(value)
			this.hasUppercase = containsUppercase.test(value)
			this.hasLowercase = containsLowercase.test(value)
			this.hasNumber = containsNumber.test(value)
		}
	},
	methods: {
		// Initialize Jwt, Email Token and User Details in Store.
		initializeAuthService(query) {
			this.$store.commit('setAuthtoken', query.AUTHSERVICE_JWT);
			this.$store.commit('setEmailToken', query.AUTHSERVICE_EMAIL_TOKEN);
			this.$store.commit('setAuthUserDetails', jwt.decode(query.AUTHSERVICE_JWT));
		},
		onSubmit() {
			// HTML Style Removal On-Submit Spam Prevention
			if (this.$v.$invalid || this.confirmPassword !== this.password || this.isAPILoading) {
				return;
			}

			let oldPass = null;
			let identityId = null;
			let identityTenant = null;
			let emailToken = null;

			// set credentials for change password
			if (this.mode === 'change') {
				oldPass = this.currentPass;
				let userToken = jwt.decode(this.$store.getters.getUserToken);
				identityId = userToken.identity.id;
				identityTenant = userToken.identity.tenant;
			} else {
				identityId = this.$store.getters.getAuthUserDetails.identity.id;
				identityTenant = this.$store.getters.getAuthUserDetails.identity.tenant;
				oldPass = this.mode === 'set' ? 'DDmX56AEZxOqL' : null; // Remove when Auth Service Configs are fixed
				emailToken = this.mode === 'reset' ? this.$store.getters.getEmailToken : null;
			}
			
			const authUserDetails = {
				email_token: emailToken,
				password: this.password,
				old_password: oldPass,
				id: identityId,
				tenant: identityTenant
			}

			if (this.mode === 'change') {
				this.$swal({
					imageHeight: '30px',
					title: 'Are you sure you want to update your password?',
					imageUrl: `${require('@/assets/images/_shared/dog-confirm.png')}`,
					showCancelButton: true,
					confirmButtonColor: '#4B8BFF',
					cancelButtonColor: '#d33',
					confirmButtonText: 'Confirm',
					cancelButtonText: 'Cancel',
					reverseButtons: true
				})
				.then(result => {
					if (result.value) {
						this.submitAPICall(authUserDetails);
					}
				});
			} else {
				this.submitAPICall(authUserDetails);
			}
		},
		submitAPICall(authUserDetails) {
			this.isAPILoading = true;
			AuthSetPasswordService.postAuthSetPass(authUserDetails)
			.then(res => {
				if (res.data.status === STATUS_MSG.AUTH_SERVICE_STATUS_SUCCESS_MSG) {
					this.isAPILoading = false;

					// set Login Note on success if Set Password.
					if (this.mode === 'set') {
						this.$store.commit('setPassSuccess', true);
						this.$store.dispatch('setPassMessage');
					}

					// Send Analytics
					const action = this.mode === 'set' ? GA.PASSWORD_PAGE.SET_PASSWORD_BUTTON.ACTION : GA.PASSWORD_PAGE.RESET_PASSWORD_BUTTON.ACTION;
					const label = this.mode === 'set' ? GA.PASSWORD_PAGE.SET_PASSWORD_BUTTON.LABEL : GA.PASSWORD_PAGE.RESET_PASSWORD_BUTTON.LABEL;

					this.sendEventTrackingToGA(action, GA.PASSWORD_PAGE.CATEGORY, label);

					if (this.mode !== 'change') {
						const token = res.data.jwt;
						const userEmail = this.$store.getters.getAuthUserDetails.identity.email;

						this.handleUserRouting(userEmail, token);
					} else {
						this.showGenericSuccessToast();
						// logout user after change password
						this.$store.dispatch('logout');
					}

					// clear store Data on success.
					this.$store.commit('clearAuthData');
				} else {
					this.isAPILoading = false;

					// clear fields on error
					this.clearFields();

					// show toast message
					this.showToastMessage();
				}
			})
		},
		handleUserRouting(email, token) {
			this.isAPILoading = true;
			this.$store.commit('setUserToken', token);

			UserService.getLoggedInUser(email)
					.then(res => {
						if (res.status === STATUS_CODES.SUCCESS) {
							this.$store.commit('clearUserToken');
							const { userRole } = res.data.userDetails;

							this.$swal({
								imageHeight: '30px',
								imageUrl: `${require('@/assets/images/_shared/success-dog.png')}`,
								imageAlt: 'Success Dog',
								title: 'Awesome!',
								text: 'You will now be redirected to the Login Page.',
								confirmButtonColor: '#4B8BFF',
								confirmButtonText: 'Got it'
							}).then(() => {
								if (userRole === USER_ROLES.ADMIN || userRole === USER_ROLES.SUPER_ADMIN) {
									window.location.href = process.env.VUE_APP_ADMIN_SITE;
								} else {
									this.$router.replace('/login');
								}
							});
						} else {
							this.showCustomErrorToast(res.data.errors[0].message,null, true);
						}

						this.isAPILoading = false;
					})
					.catch(() => {
						this.showGenericErrorToast();
						this.isAPILoading = false;
					});
		},
		setChangePasswordText() {
			return this.mode === 'change' ? 'New' : '';
		},
		onBlur(field) {
			if (this.mode === 'change') {
				let details = {
					currentPass: this.currentPass,
					password: this.password,
					confirmPass: this.confirmPassword
				}
				this.$emit('changePass', details);
			}

			switch(field) {
				case 'currentPass':
					this.$v.currentPass.$touch();
					break;

				case 'password':
					this.$v.password.$touch();
					break;

				default:
					this.$v.confirmPassword.$touch();
			}
		},
		clearFields() {
			this.currentPass = '';
			this.password = '';
			this.confirmPassword = '';
			this.showPassword = false;
			this.showCurrentPassword = false;
			this.$v.$reset();
		},
		showToastMessage() {
			let title = '';
			let message = '';
			let isNoText = false;
			const errorMessage = this.toastMessages.password();

			if (this.mode === 'change') {
				title = errorMessage.ERROR_CHANGE_PASSWORD;
				isNoText = true;
			} else {
				if (this.mode === 'reset') {
					title = errorMessage.RESET_PASSWORD_FAILED_TITLE;
				} else {
					title = errorMessage.SET_PASSWORD_FAILED_TITLE;
				}
				message = ERROR_MSG.INTERNAL_ERROR_MSG;
			}
			
			this.showCustomErrorToast(title, message, isNoText);
		},
		setShowPassword() {
			this.showPassword = !this.showPassword;
		},

		setShowCurrentPassword() {
			this.showCurrentPassword = !this.showCurrentPassword;
		},

		setShowConfirmPassword() {
			this.showConfirmPassword = !this.showConfirmPassword;
		}
	},
	validations() {
		return this.validationRules;
	}
}